import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from "firebase/firestore";
import { FaCheck, FaFileExcel, FaFilePdf, FaMagic, FaMoneyBillWave, FaSync, FaUserTie } from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

const MESES = [
  { key: "enero", label: "Enero" },
  { key: "febrero", label: "Febrero" },
  { key: "marzo", label: "Marzo" },
  { key: "abril", label: "Abril" },
  { key: "mayo", label: "Mayo" },
  { key: "junio", label: "Junio" },
  { key: "julio", label: "Julio" },
  { key: "agosto", label: "Agosto" },
  { key: "septiembre", label: "Septiembre" },
  { key: "octubre", label: "Octubre" },
  { key: "noviembre", label: "Noviembre" },
  { key: "diciembre", label: "Diciembre" },
];

function Pagos({ usuario }) {
  const [loading, setLoading] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [pagos, setPagos] = useState({});
  const [procesandoVoucher, setProcesandoVoucher] = useState(false);

  const docId = useMemo(() => `${usuario?.ruc || ""}_${anio}`, [usuario?.ruc, anio]);
  const totalEmpleados = empleados.length;

  const cargarEmpleados = async () => {
    if (!usuario?.ruc) return;
    const q = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", usuario.ruc), where("activo", "==", true));
    const snap = await getDocs(q);
    const list = [];
    snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
    list.sort((a, b) => `${a.apellidos || ""} ${a.nombres || ""}`.localeCompare(`${b.apellidos || ""} ${b.nombres || ""}`));
    setEmpleados(list);
  };

  const cargarPagos = async () => {
    if (!usuario?.ruc) return;
    const ref = doc(db, "pl_pagos_salario", docId);
    const snap = await getDoc(ref);
    setPagos(snap.exists() ? snap.data()?.pagos || {} : {});
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      await Promise.all([cargarEmpleados(), cargarPagos()]);
    } catch (error) {
      console.error("Error al cargar pagos:", error);
      Swal.fire("Error", "No se pudo cargar el módulo de pagos.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [usuario?.ruc, anio]);

  const guardarPagos = async (nextPagos) => {
    setPagos(nextPagos);
    await setDoc(
      doc(db, "pl_pagos_salario", docId),
      {
        ruc_empresa: usuario?.ruc || "",
        anio,
        pagos: nextPagos,
        updated_at: new Date(),
      },
      { merge: true }
    );
  };

  const normalizar = (txt) =>
    String(txt || "")
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const MESES_INDEX = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];

  // Regla de negocio: primeros N días del mes corresponden al mes anterior.
  const DIAS_CORTE_MES_ANTERIOR = 7;

  const extraerFechaVoucher = (texto) => {
    const raw = String(texto || "");
    // Formatos comunes: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy, yyyy-mm-dd
    const matchDMY = raw.match(/\b([0-3]?\d)[\/\-.]([01]?\d)[\/\-.](20\d{2})\b/);
    if (matchDMY) {
      const day = Number(matchDMY[1]);
      const month = Number(matchDMY[2]);
      const year = Number(matchDMY[3]);
      const dt = new Date(year, month - 1, day);
      if (!Number.isNaN(dt.getTime())) return dt;
    }

    const matchYMD = raw.match(/\b(20\d{2})[\/\-.]([01]?\d)[\/\-.]([0-3]?\d)\b/);
    if (matchYMD) {
      const year = Number(matchYMD[1]);
      const month = Number(matchYMD[2]);
      const day = Number(matchYMD[3]);
      const dt = new Date(year, month - 1, day);
      if (!Number.isNaN(dt.getTime())) return dt;
    }

    return null;
  };

  const resolverMesYAnioDesdeFechaPago = (fechaPago) => {
    const dt = new Date(fechaPago);
    if (dt.getDate() <= DIAS_CORTE_MES_ANTERIOR) {
      dt.setMonth(dt.getMonth() - 1);
    }
    return {
      anioPago: dt.getFullYear(),
      mesKey: MESES_INDEX[dt.getMonth()],
    };
  };

  const contieneNombre = (textoNormalizado, nombreCompleto) => {
    const tokens = normalizar(nombreCompleto).split(" ").filter((t) => t.length >= 3);
    if (tokens.length < 2) return false;
    return tokens.every((t) => textoNormalizado.includes(t));
  };

  const extraerTextoPDF = async (file) => {
    const pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;

    let texto = "";
    for (let p = 1; p <= pdf.numPages; p += 1) {
      const page = await pdf.getPage(p);
      const content = await page.getTextContent();
      const pageText = content.items.map((i) => i.str || "").join(" ");
      texto += `\n${pageText}`;
    }

    if (normalizar(texto).length > 50) return texto;

    // Fallback OCR para vouchers escaneados (sin capa de texto).
    const { createWorker } = await import("tesseract.js");
    const worker = await createWorker("spa+eng");
    let ocrText = "";
    const maxPagesOCR = Math.min(pdf.numPages, 2);
    for (let p = 1; p <= maxPagesOCR; p += 1) {
      const page = await pdf.getPage(p);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext("2d");
      await page.render({ canvasContext: context, viewport }).promise;
      const { data } = await worker.recognize(canvas);
      ocrText += `\n${data.text || ""}`;
    }
    await worker.terminate();
    return ocrText;
  };

  const procesarVoucherInterbank = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setProcesandoVoucher(true);
      const text = await extraerTextoPDF(file);
      const voucherText = normalizar(text);
      const fechaVoucher = extraerFechaVoucher(text);

      if (!voucherText) {
        throw new Error("No se pudo extraer texto del voucher.");
      }
      if (!fechaVoucher) {
        throw new Error("No se pudo detectar la fecha del voucher.");
      }

      const { anioPago, mesKey } = resolverMesYAnioDesdeFechaPago(fechaVoucher);

      const matched = [];
      const targetDocId = `${usuario?.ruc || ""}_${anioPago}`;
      const targetDocRef = doc(db, "pl_pagos_salario", targetDocId);
      const targetSnap = await getDoc(targetDocRef);
      const pagosBase = targetSnap.exists() ? targetSnap.data()?.pagos || {} : {};
      const next = { ...pagosBase };

      for (const emp of empleados) {
        const dni = String(emp.dni || emp.id || "").trim();
        const nombreDirecto = `${emp.nombres || ""} ${emp.apellidos || ""}`.trim();
        const nombreInvertido = `${emp.apellidos || ""} ${emp.nombres || ""}`.trim();

        const coincidePorDni = dni ? voucherText.includes(normalizar(dni)) : false;
        const coincidePorNombre =
          contieneNombre(voucherText, nombreDirecto) || contieneNombre(voucherText, nombreInvertido);

        if (coincidePorDni || coincidePorNombre) {
          next[emp.id] = {
            ...(next[emp.id] || {}),
            [mesKey]: true,
          };
          matched.push(emp);
        }
      }

      if (matched.length === 0) {
        Swal.fire("Sin coincidencias", "No se detectaron empleados en el voucher de Interbank.", "warning");
        return;
      }
      await setDoc(
        targetDocRef,
        {
          ruc_empresa: usuario?.ruc || "",
          anio: anioPago,
          pagos: next,
          updated_at: new Date(),
        },
        { merge: true }
      );

      if (anioPago === anio) {
        setPagos(next);
      }

      const mesLabel = MESES.find((m) => m.key === mesKey)?.label || mesKey;
      Swal.fire(
        "Éxito",
        `Se marcaron ${matched.length} empleado(s) en ${mesLabel} ${anioPago}. Fecha detectada: ${fechaVoucher.toLocaleDateString("es-PE")}.`,
        "success"
      );
    } catch (error) {
      console.error("Error procesando voucher:", error);
      Swal.fire("Error", error?.message || "No se pudo procesar el voucher del banco.", "error");
    } finally {
      setProcesandoVoucher(false);
      e.target.value = "";
    }
  };

  const exportarExcel = () => {
    const rows = empleados.map((emp, idx) => {
      const totalPagados = MESES.reduce(
        (acc, m) => acc + (Boolean(pagos?.[emp.id]?.[m.key]) ? 1 : 0),
        0
      );
      const row = {
        N: idx + 1,
        DNI: emp.dni || emp.id || "",
        "Nombres y Apellidos": `${emp.nombres || ""} ${emp.apellidos || ""}`.trim(),
      };
      MESES.forEach((m) => {
        row[m.label] = Boolean(pagos?.[emp.id]?.[m.key]) ? "SI" : "NO";
      });
      row["Total Pagado"] = totalPagados;
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 34 },
      ...MESES.map(() => ({ wch: 11 })),
      { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Pagos_${anio}`);
    XLSX.writeFile(wb, `pagos-salario-${usuario?.ruc || "empresa"}-${anio}.xlsx`);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {(loading || procesandoVoucher) && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
              <p className="text-white mt-3 mb-0">
                {procesandoVoucher ? "Procesando voucher..." : "Procesando solicitud..."}
              </p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaMoneyBillWave className="me-3" />
                Pagos de Salario
              </h2>
              <p className="mb-0 opacity-75">
                Marcación automática de pagos desde vouchers bancarios
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaUserTie size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Control de Pagos</h5>
                    <p className="text-muted mb-0 small">
                      Se detecta automáticamente empleado y mes según fecha del voucher
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <Badge bg="light" text="dark" className="px-3 py-2 border">
                      {empleados.length} empleados
                    </Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="align-items-end g-3">
                  <Col md={3}>
                    <Form.Label className="fw-semibold text-muted small text-uppercase">Año</Form.Label>
                    <Form.Control
                      type="number"
                      min="2000"
                      max="2100"
                      value={anio}
                      onChange={(e) => setAnio(Number(e.target.value || new Date().getFullYear()))}
                      style={{ borderRadius: "10px" }}
                    />
                  </Col>
                  <Col md={9} className="text-end d-flex justify-content-end gap-2">
                    <label className={`btn ${procesandoVoucher ? "btn-secondary" : "btn-primary"} mb-0`} style={{ borderRadius: "10px" }}>
                      {procesandoVoucher ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Procesando voucher...
                        </>
                      ) : (
                        <>
                          <FaFilePdf className="me-2" />
                          Adjuntar boucher de banco
                        </>
                      )}
                      <input
                        type="file"
                        accept=".pdf"
                        className="d-none"
                        onChange={procesarVoucherInterbank}
                        disabled={procesandoVoucher || empleados.length === 0}
                      />
                    </label>
                    <Button
                      variant="success"
                      onClick={exportarExcel}
                      disabled={empleados.length === 0}
                      style={{ borderRadius: "10px" }}
                    >
                      <FaFileExcel className="me-1" />
                      Exportar Excel
                    </Button>
                    <Button variant="outline-secondary" onClick={cargarDatos} disabled={loading} style={{ borderRadius: "10px" }}>
                      <FaSync className="me-1" />
                      Recargar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="mb-3">
              <Badge bg="info" className="me-2">
                <FaMagic className="me-1" />
                Modo automático por voucher
              </Badge>
              <small className="text-muted">
                Detecta fecha del voucher + empleado y marca automáticamente el mes. Si la fecha está entre el día 1 y {DIAS_CORTE_MES_ANTERIOR}, se registra en el mes anterior.
              </small>
            </div>

            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "18px", overflow: "hidden" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  borderRadius: "18px 18px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaMoneyBillWave className="me-2" />
                  <div className="flex-grow-1">
                    <h5 className="mb-0 fw-semibold">Tabla de Pagos Anuales</h5>
                    <small className="opacity-75">Checks automáticos por voucher</small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 40%)" }}>
                <div style={{ maxHeight: "68vh", overflow: "auto", borderTop: "1px solid #f1f5f9" }}>
                  <Table bordered={false} hover size="sm" className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead
                      style={{
                        background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                        position: "sticky",
                        top: 0,
                        zIndex: 30,
                        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
                      }}
                    >
                      <tr>
                        <th style={{ minWidth: 70, textAlign: "center", color: "#334155" }}>N°</th>
                        <th style={{ minWidth: 130, color: "#334155" }}>DNI</th>
                        <th style={{ minWidth: 280, color: "#334155" }}>Nombres y Apellidos</th>
                        {MESES.map((m) => (
                          <th key={m.key} className="text-center" style={{ minWidth: 98, color: "#334155" }}>
                            {m.label}
                          </th>
                        ))}
                        <th className="text-center" style={{ minWidth: 120, color: "#334155" }}>
                          Total Pagado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {empleados.map((emp, idx) => (
                        <tr key={emp.id} style={{ borderBottom: "1px solid #eef2f7" }}>
                          <td
                            style={{
                              textAlign: "center",
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              background: "#ffffff",
                            }}
                          >
                            <Badge bg="light" text="dark" className="border px-2 py-1">
                              {idx + 1}
                            </Badge>
                          </td>
                          <td
                            className="fw-medium text-dark"
                            style={{
                              position: "sticky",
                              left: 70,
                              zIndex: 20,
                              background: "#ffffff",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Badge bg="light" text="dark" className="border px-2 py-1">
                              {emp.dni || emp.id || "-"}
                            </Badge>
                          </td>
                          <td
                            className="fw-medium text-dark"
                            style={{
                              position: "sticky",
                              left: 200,
                              zIndex: 20,
                              background: "#ffffff",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`${emp.nombres || ""} ${emp.apellidos || ""}`.trim()}
                          </td>
                          {MESES.map((m) => (
                            <td key={`${emp.id}_${m.key}`} className="text-center" style={{ padding: "10px 6px" }}>
                              {Boolean(pagos?.[emp.id]?.[m.key]) ? (
                                <span
                                  className="d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 999,
                                    background: "linear-gradient(135deg, #10b981 0%, #22c55e 100%)",
                                    color: "white",
                                    boxShadow: "0 4px 10px rgba(16, 185, 129, 0.35)",
                                  }}
                                >
                                  <FaCheck size={12} />
                                </span>
                              ) : (
                                <span
                                  className="d-inline-block"
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 999,
                                    border: "2px solid #cbd5e1",
                                    background: "#f8fafc",
                                  }}
                                />
                              )}
                            </td>
                          ))}
                          <td className="text-center">
                            <Badge bg="dark" className="px-2 py-1">
                              {MESES.reduce(
                                (acc, m) => acc + (Boolean(pagos?.[emp.id]?.[m.key]) ? 1 : 0),
                                0
                              )}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {empleados.length === 0 && (
                        <tr>
                          <td colSpan={14} className="text-center text-muted py-4">
                            No hay empleados activos.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot
                      style={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 25,
                        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                        boxShadow: "0 -2px 8px rgba(15, 23, 42, 0.1)",
                      }}
                    >
                      <tr>
                        <th className="text-center">Σ</th>
                        <th>DNI</th>
                        <th>Total por mes</th>
                        {MESES.map((m) => {
                          const totalMes = empleados.reduce(
                            (acc, emp) => acc + (Boolean(pagos?.[emp.id]?.[m.key]) ? 1 : 0),
                            0
                          );
                          const completo = totalEmpleados > 0 && totalMes === totalEmpleados;
                          return (
                            <th key={`sum_${m.key}`} className="text-center">
                              <div className="d-flex flex-column align-items-center">
                                <Badge bg={completo ? "success" : "secondary"} className="px-2 py-1">
                                  {totalMes}/{totalEmpleados}
                                </Badge>
                                <small className={completo ? "text-success" : "text-muted"}>
                                  {completo ? "Completo" : "Pendiente"}
                                </small>
                              </div>
                            </th>
                          );
                        })}
                        <th className="text-center">
                          <Badge bg="dark" className="px-2 py-1">
                            {MESES.reduce((sumMeses, m) => {
                              const totalMes = empleados.reduce(
                                (acc, emp) => acc + (Boolean(pagos?.[emp.id]?.[m.key]) ? 1 : 0),
                                0
                              );
                              return sumMeses + totalMes;
                            }, 0)}
                          </Badge>
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default Pagos;
